import { alpha, createTheme, type Shadows, type Theme, type ThemeOptions } from '@mui/material/styles';
import {
  CONTENT_ACTION,
  CONTENT_BORDERS,
  CONTENT_PALETTE,
  CONTENT_SHAPE,
  type AppColorMode,
} from '../constants/contentColors';
import { createContentTypography } from '../constants/contentTypography';
import { CONTENT_FONT_FAMILY_VAR } from '../constants/fonts';

export interface ContentThemeOptions {
  /** True when the UI language uses Latin script (enables negative tracking on headings). */
  latin: boolean;
}

/**
 * Near-flat elevation: separation comes from hairline borders, whitespace and
 * typography. Only floating surfaces (menus, popovers, dialogs, map panels)
 * keep a soft shadow.
 */
function createContentShadows(mode: AppColorMode): Shadows {
  const ink = mode === 'dark' ? '0, 0, 0' : '15, 23, 42';
  const low = mode === 'dark' ? 'none' : `0 1px 2px rgba(${ink}, 0.04)`;
  const mid = mode === 'dark' ? 'none' : `0 2px 6px rgba(${ink}, 0.06)`;
  const float = `0 8px 24px rgba(${ink}, ${mode === 'dark' ? 0.45 : 0.1})`;
  const modal = `0 24px 48px rgba(${ink}, ${mode === 'dark' ? 0.55 : 0.14})`;
  return Array.from({ length: 25 }, (_, i) => {
    if (i === 0) return 'none';
    if (i <= 3) return low;
    if (i <= 7) return mid;
    if (i <= 16) return float;
    return modal;
  }) as Shadows;
}

/** Hairline tokens with a safe fallback when rendered outside the content theme. */
export function contentBorders(theme: Theme) {
  return (
    theme.palette.border ?? {
      subtle: theme.palette.divider,
      default: theme.palette.divider,
      strong: theme.palette.divider,
      rule: theme.palette.text.primary,
    }
  );
}

/**
 * MUI theme for the ZERO content area (everything rendered inside the shell
 * tile). Built from scratch rather than composed on the shell theme so no
 * shell tokens (gradients, hover lifts, Inter) leak into the content; the
 * shell keeps its inline theme in `App.tsx` untouched.
 *
 * Portals opened from content (dialogs, menus, popovers, bottom sheets,
 * tooltips) render under `<body>`, which keeps the shell font, so their papers
 * set the content font explicitly.
 */
export function createContentTheme(
  mode: AppColorMode,
  direction: 'ltr' | 'rtl',
  { latin }: ContentThemeOptions,
) {
  const p = CONTENT_PALETTE[mode];
  const borders = CONTENT_BORDERS[mode];
  const action = CONTENT_ACTION[mode];
  const isDark = mode === 'dark';
  const shadows = createContentShadows(mode);
  const portalFont = { fontFamily: CONTENT_FONT_FAMILY_VAR };

  const semanticAlert = (c: { main: string; light: string; dark: string }) => ({
    backgroundColor: alpha(c.main, isDark ? 0.1 : 0.06),
    borderColor: alpha(c.main, 0.24),
    color: isDark ? c.light : c.dark,
    '& .MuiAlert-icon': { color: c.main },
  });

  // Ink tooltip in light mode, off-white in dark mode (inverse of the page).
  const tooltipBg = p.text.primary;
  const tooltipFg = isDark ? '#0F172A' : '#FFFFFF';

  const options: ThemeOptions = {
    direction,
    palette: {
      mode,
      primary: { ...p.primary },
      secondary: { ...p.secondary },
      success: { ...p.success },
      warning: { ...p.warning },
      error: { ...p.error },
      info: { ...p.info },
      text: { ...p.text },
      background: { ...p.background },
      divider: borders.default,
      border: { ...borders },
      action: {
        hover: action.hover,
        selected: action.selected,
        focus: action.focusRing,
      },
    },
    shape: { borderRadius: CONTENT_SHAPE.xs },
    shadows,
    typography: createContentTypography(latin),
    components: {
      MuiPaper: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            backgroundImage: 'none',
            ...(ownerState.variant === 'elevation' && (ownerState.elevation ?? 1) > 0
              ? { border: `1px solid ${borders.subtle}` }
              : {}),
          }),
          rounded: { borderRadius: CONTENT_SHAPE.md },
          outlined: { borderColor: borders.default },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: CONTENT_SHAPE.md,
            border: `1px solid ${borders.default}`,
            boxShadow: 'none',
            backgroundImage: 'none',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 20,
            '&:last-child': { paddingBottom: 20 },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: CONTENT_SHAPE.sm,
            textTransform: 'none',
            fontWeight: 600,
            padding: '7px 16px',
            boxShadow: 'none',
            transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
          },
          sizeSmall: { padding: '4px 10px' },
          contained: {
            '&:hover': { boxShadow: 'none' },
          },
          outlined: {
            borderColor: borders.strong,
            '&:hover': {
              borderColor: p.text.primary,
              backgroundColor: action.hover,
            },
          },
          outlinedInherit: {
            borderColor: borders.strong,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: CONTENT_SHAPE.sm },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: CONTENT_SHAPE.sm,
            backgroundColor: p.background.input,
            transition: 'box-shadow 160ms ease',
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: borders.strong },
            '&.Mui-focused': {
              backgroundColor: p.background.inputFocused,
              boxShadow: `0 0 0 3px ${action.focusRing}`,
            },
          },
          notchedOutline: { borderColor: borders.default },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44 },
          indicator: { height: 2, borderRadius: 0 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 44,
            minWidth: 0,
            paddingInline: 2,
            marginInlineEnd: 28,
            textTransform: 'none',
            fontSize: '0.9375rem',
            fontWeight: 500,
            color: p.text.secondary,
            '&.Mui-selected': { color: p.text.primary, fontWeight: 600 },
            '&:last-of-type': { marginInlineEnd: 0 },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: borders.subtle,
            fontSize: '0.875rem',
            paddingTop: 12,
            paddingBottom: 12,
            fontVariantNumeric: 'tabular-nums',
          },
          head: {
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: p.text.secondary,
            backgroundColor: 'transparent',
            borderBottom: `1px solid ${borders.strong}`,
            whiteSpace: 'nowrap',
          },
          stickyHeader: { backgroundColor: p.background.paper },
          sizeSmall: { paddingTop: 8, paddingBottom: 8 },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': { backgroundColor: action.hover },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: CONTENT_SHAPE.xs, fontWeight: 500, fontSize: '0.75rem' },
          sizeMedium: { height: 28 },
          sizeSmall: { height: 22 },
          outlined: { borderColor: borders.strong },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: {
            ...portalFont,
            backgroundColor: tooltipBg,
            color: tooltipFg,
            fontSize: '0.75rem',
            lineHeight: 1.45,
            borderRadius: CONTENT_SHAPE.xs,
            padding: '6px 10px',
            maxWidth: 320,
          },
          arrow: { color: tooltipBg },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { backgroundColor: alpha(p.text.primary, isDark ? 0.08 : 0.06) },
          rounded: { borderRadius: CONTENT_SHAPE.xs },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: CONTENT_SHAPE.sm,
            textTransform: 'none',
            borderColor: borders.default,
            '&.Mui-selected': {
              backgroundColor: action.selected,
              color: p.primary.main,
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: borders.default },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: CONTENT_SHAPE.sm,
            border: '1px solid transparent',
          },
          standardSuccess: semanticAlert(p.success),
          standardError: semanticAlert(p.error),
          standardWarning: semanticAlert(p.warning),
          standardInfo: semanticAlert(p.info),
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 4,
            borderRadius: 2,
            backgroundColor: alpha(p.text.primary, isDark ? 0.12 : 0.08),
          },
          bar: { borderRadius: 2 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            ...portalFont,
            borderRadius: CONTENT_SHAPE.md,
            border: `1px solid ${borders.default}`,
            boxShadow: shadows[24],
            backgroundImage: 'none',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontSize: '1.125rem', fontWeight: 600 },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { ...portalFont, backgroundImage: 'none' },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { ...portalFont, borderRadius: CONTENT_SHAPE.sm },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { ...portalFont, borderRadius: CONTENT_SHAPE.sm },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { fontSize: '0.875rem', minHeight: 36 },
        },
      },
      MuiAccordion: {
        defaultProps: { disableGutters: true, elevation: 0 },
        styleOverrides: {
          root: {
            '&::before': { display: 'none' },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { textDecorationColor: alpha(p.primary.main, 0.4) },
        },
      },
    },
  };

  return createTheme(options);
}
